import { useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Maximize2, X as XIcon } from 'lucide-react';
import InteractiveMermaid from './InteractiveMermaid';
import { buildReactFlowHtml } from './buildReactFlowHtml';

// ============================================================
// Mermaid diagram renderer (delegates to InteractiveMermaid)
// ============================================================
export function MermaidDiagram({ chart }: { chart: string }) {
  return <InteractiveMermaid chart={chart} />;
}

// ============================================================
// Color-coding legends for status-annotated diagrams
// ============================================================
type DiagramLegendType = 'threat' | 'compliance';

const LEGEND_ITEMS: Record<DiagramLegendType, { color: string; label: string }[]> = {
  threat: [
    { color: '#ef4444', label: 'Potential Threat' },
    { color: '#f59e0b', label: 'Mitigated' },
    { color: '#22c55e', label: 'Safe' },
  ],
  compliance: [
    { color: '#ef4444', label: 'Non-Compliant' },
    { color: '#f59e0b', label: 'Partial' },
    { color: '#22c55e', label: 'Compliant' },
  ],
};

function DiagramLegend({ type }: { type: DiagramLegendType }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
      {LEGEND_ITEMS[type].map((item) => (
        <Stack key={item.label} direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, boxShadow: `0 0 6px ${alpha(item.color, 0.6)}` }} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>{item.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// ============================================================
// ReactFlow diagram renderer for extraction page (with fullscreen)
// ============================================================
export function ReactFlowDiagram({ json, legend }: { json: string; legend?: DiagramLegendType }) {
  const theme = useTheme();
  const [fullscreen, setFullscreen] = useState(false);

  let htmlContent = '';
  try {
    JSON.parse(json);
    htmlContent = buildReactFlowHtml(json, theme.palette.mode);
  } catch {
    return null;
  }

  return (
    <>
      <Box sx={{ position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <iframe
          srcDoc={htmlContent}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
          title="ReactFlow diagram"
        />
        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            position: 'absolute', top: 8, right: 8,
            bgcolor: alpha(theme.palette.common.black, 0.6), borderRadius: 1.5,
            px: 0.5, py: 0.25, backdropFilter: 'blur(8px)',
          }}
        >
          <IconButton size="small" onClick={() => setFullscreen(true)} sx={{ color: theme.palette.common.white, p: 0.5 }}>
            <Maximize2 size={16} />
          </IconButton>
        </Stack>
      </Box>
      {legend && <DiagramLegend type={legend} />}

      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullScreen PaperProps={{ sx: { bgcolor: theme.palette.background.paper } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography fontWeight={600} fontSize={14}>Interactive Diagram</Typography>
          <IconButton size="small" onClick={() => setFullscreen(false)}>
            <XIcon size={18} />
          </IconButton>
        </Box>
        <iframe
          srcDoc={htmlContent}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', flex: 1, border: 'none', display: 'block' }}
          title="ReactFlow diagram fullscreen"
        />
        {legend && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <DiagramLegend type={legend} />
          </Box>
        )}
      </Dialog>
    </>
  );
}

// ============================================================
// Smart diagram: detects JSON (ReactFlow) vs string (Mermaid)
// ============================================================
export function SmartDiagram({ chart, legend }: { chart: string; legend?: DiagramLegendType }) {
  const trimmed = chart.trim();
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && (parsed.nodes || parsed.edges)) {
      return <ReactFlowDiagram json={trimmed} legend={legend} />;
    }
  } catch {}
  return <MermaidDiagram chart={chart} />;
}
